/**
 * Settings Page
 *
 * Comprehensive settings management for user preferences:
 * - Language selection and locale settings
 * - Password change and security
 * - Email account connections (OAuth)
 * - AI Assistant preferences (TASK-286)
 *
 * TASK-168: Frontend Settings Page UI
 * TASK-286: Added AI Assistant settings tab
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Globe, Lock, Mail } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import LanguageSettings from '../components/settings/LanguageSettings';
import SecuritySettings from '../components/settings/SecuritySettings';
// TASK-852: `EmailConnectionSettings` STAYS MOUNTED, and unmounting it was reverted.
//
// The tab was removed once Data Sources rendered Gmail and MS Graph as cards — but rendering
// is not connecting. Review found the replacement cannot complete an OAuth connect: `connect`
// returns before writing a row, so a live mailbox reports `connected: false` while it is
// syncing; the auth URL redirects to a callback that stores nothing and expects a popup
// opener the new page does not create; and it demands per-account vendor credentials the
// deployment already holds (422 on this box, for a provider that works today).
//
// Removing the only working connect flow before its replacement worked is the flag day the
// ticket's own step 5 was ordered to avoid. It goes back until the arm is real.
import { EmailConnectionSettings } from '../components/settings/EmailConnectionSettings';
import { DataSourcesSettings } from '../components/settings/DataSourcesSettings';
import SecondaryNav from '../components/layout/SecondaryNav';

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const [activeTab, setActiveTab] = useState('language');

  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />
      <div className="max-w-4xl mx-auto p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('title', 'Settings')}
        </h1>
        <p className="text-gray-600 mt-2">
          {t('subtitle', 'Manage your preferences and account settings')}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full mb-6">
          <TabsTrigger value="language" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>{t('tabs.language', 'Language')}</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span>{t('tabs.security', 'Security')}</span>
          </TabsTrigger>
          {/* TASK-851: the fourth tab. `grid-cols-4` was already declared here against three
              triggers — a layout leftover the ticket noted, and the column this fills. */}
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <span>{t('tabs.email', 'Email')}</span>
          </TabsTrigger>
          <TabsTrigger value="sources" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            <span>{t('tabs.sources', 'Data Sources')}</span>
          </TabsTrigger>
          {/* TASK-713: the AI-assistant tab died with the floating GenAIChat —
              the dock has no per-user settings (always on, one placement rule). */}
        </TabsList>

        <TabsContent value="language">
          <LanguageSettings />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySettings />
        </TabsContent>

        <TabsContent value="email">
          <EmailConnectionSettings />
        </TabsContent>

        <TabsContent value="sources">
          <DataSourcesSettings />
        </TabsContent>

      </Tabs>
      </div>
    </div>
  );
}
